import { useEffect, useRef, useState } from "react";
import { LuCheck, LuSearch, LuUser, LuUserPlus } from "react-icons/lu";
import { TpvDialog } from "../../../components/TpvDialog";
import { listTpvCustomers, createTpvCustomer } from "../../../api/tpvCustomers";
export const CustomerPicker = ({
  selected,
  isDefault = false,
  onSelect,
  onDefault,
  onClose,
  onError,
}) => {
  const [query, setQuery] = useState(""),
    [page, setPage] = useState(1),
    [rows, setRows] = useState([]),
    [pages, setPages] = useState(0),
    [status, setStatus] = useState("loading"),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [creating, setCreating] = useState(false),
    [retry, setRetry] = useState(0);
  const [fullname, setFullname] = useState(""),
    [email, setEmail] = useState(""),
    [phone, setPhone] = useState("");
  const pending = useRef(false),
    sequence = useRef(0),
    nameRef = useRef(null);
  useEffect(() => {
    let alive = true;
    const id = ++sequence.current;
    setStatus("loading");
    const timer = setTimeout(async () => {
      try {
        const response = await listTpvCustomers({ search: query, page });
        if (!alive || id !== sequence.current) return;
        if (
          response?.error !== false ||
          !Array.isArray(response.data?.customers)
        )
          throw Error();
        setRows(response.data.customers);
        setPages(response.data.totalPages);
        setStatus("ready");
      } catch {
        if (alive) setStatus("error");
      }
    }, 200);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [query, page, retry]);
  useEffect(() => {
    if (creating) nameRef.current?.focus();
  }, [creating]);
  const choose = async (customer, isDefault = false) => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      await (isDefault ? onDefault() : onSelect(customer));
    } catch (e) {
      const message = e.message || "No se pudo seleccionar el cliente.";
      setError(message);
      onError?.(message);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  const save = async (event) => {
    event.preventDefault();
    if (pending.current) return;
    if (!event.currentTarget.checkValidity() || !fullname.trim()) {
      setError("Completa el nombre y un email válido.");
      return;
    }
    pending.current = true;
    setBusy(true);
    setError("");
    let created = false;
    try {
      const response = await createTpvCustomer({
        fullname: fullname.trim(),
        email: email.trim(),
        phone: phone.trim(),
      });
      if (response?.error !== false || !response.data?.customerId)
        throw Error(
          response?.message ||
            "No se pudo confirmar el alta. Busca el cliente antes de repetir.",
        );
      created = true;
      setCreating(false);
      setRows((previous) => [response.data, ...previous]);
      setFullname("");
      setEmail("");
      setPhone("");
      await onSelect(response.data);
    } catch (e) {
      const message =
        (created ? "El cliente se ha creado. Selecciónalo en la lista. " : "") +
        (e.message || "No se pudo guardar el cliente.");
      setError(message);
      onError?.(message);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  return (
    <TpvDialog
      title="Cliente"
      onClose={onClose}
      busy={busy}
      className="tpvCustomerDialog"
    >
      <div className="tpvDialogBody">
        <button
          className="tpvCustomerCash"
          aria-pressed={isDefault}
          disabled={busy}
          onClick={() => choose(null, true)}
        >
          <LuUser aria-hidden="true" />
          <strong>Cliente contado</strong>
          {isDefault && <LuCheck aria-hidden="true" />}
        </button>
        <label className="tpvCustomerSearch">
          <LuSearch aria-hidden="true" />
          <input
            type="search"
            aria-label="Teléfono, nombre o email"
            placeholder="Teléfono, nombre o email"
            value={query}
            disabled={busy}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
          />
        </label>
        <h3>{query ? "Resultados de búsqueda" : "Clientes del negocio"}</h3>
        {error && (
          <p role="alert" className="tpvDialogError">
            {error}
          </p>
        )}
        {status === "loading" ? (
          <p role="status">Buscando clientes…</p>
        ) : status === "error" ? (
          <div role="alert">
            No se pudieron cargar los clientes.{" "}
            <button disabled={busy} onClick={() => setRetry(retry + 1)}>
              Reintentar
            </button>
          </div>
        ) : !rows.length ? (
          <p>No hay clientes que coincidan.</p>
        ) : (
          <div className="tpvCustomerRows">
            {rows.map((customer) => (
              <button
                key={customer.customerId}
                disabled={busy}
                onClick={() => choose(customer)}
                aria-pressed={selected?.customerId === customer.customerId}
              >
                <span className="tpvCustomerAvatar">
                  {(customer.fullname || "C")
                    .split(" ")
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((part) => part[0])
                    .join("")}
                </span>
                <span>
                  <strong>{customer.fullname || "Sin nombre"}</strong>
                  <small>{customer.phone || customer.email}</small>
                </span>
                <small className="tpvCustomerCount">
                  {Number.isFinite(customer.orderCount)
                    ? `${customer.orderCount} pedidos`
                    : ""}
                </small>
                {selected?.customerId === customer.customerId && (
                  <LuCheck aria-hidden="true" />
                )}
              </button>
            ))}
          </div>
        )}
        {status === "ready" && pages > 1 && (
          <div className="tpvCustomerPagination">
            <button
              disabled={busy || page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Anterior
            </button>
            <span>
              {page} / {pages}
            </span>
            <button
              disabled={busy || page >= pages}
              onClick={() => setPage(page + 1)}
            >
              Siguiente
            </button>
          </div>
        )}
        {creating && (
          <form onSubmit={save} noValidate className="tpvCustomerCreate">
            <h3>Nuevo cliente</h3>
            <label>
              Nombre · Obligatorio
              <input
                ref={nameRef}
                autoComplete="name"
                required
                maxLength={120}
                value={fullname}
                disabled={busy}
                onChange={(e) => setFullname(e.target.value)}
              />
            </label>
            <label>
              Teléfono
              <input
                type="tel"
                autoComplete="tel"
                value={phone}
                disabled={busy}
                onChange={(e) => setPhone(e.target.value)}
              />
            </label>
            <label>
              Email · Obligatorio
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                disabled={busy}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <div>
              <button
                type="button"
                disabled={busy}
                onClick={() => setCreating(false)}
              >
                Cancelar alta
              </button>
              <button className="tpvPrimary" disabled={busy}>
                {busy ? "Guardando…" : "Guardar y seleccionar cliente"}
              </button>
            </div>
          </form>
        )}
      </div>
      <footer>
        <button
          className="tpvCreateCustomerButton"
          disabled={busy || creating}
          onClick={() => {
            setCreating(true);
            setError("");
          }}
        >
          <LuUserPlus aria-hidden="true" /> Crear cliente
        </button>
      </footer>
    </TpvDialog>
  );
};
